import * as React from 'react';
import { asRows, toMember } from './rows';
import { asRpcError, useLiveQuery } from './rpc';
import type { Member } from './types';
import { activeAdminCount } from '@/lib/teamRules';

/**
 * The office team of the current agency, read once per agency (design section 3): `agency_team` for
 * every active member, inactive members included so their names stay on past records. Consumers: the
 * Team screen, the Carriers Dispatcher column, the Broker payments Dispatcher column, "recorded by" in
 * both payment histories. A null answer is an empty team.
 */

export interface Team {
  status: 'loading' | 'error' | 'ready';
  members: Member[];
  byId: Map<string, Member>;
  /** displayName, else the email; null for an unknown id (a removed account: "Former member"). */
  nameOf(userId: string | null | undefined): string | null;
  /** The ACTIVE dispatchers whose carriers include `carrierId`. */
  dispatchersOf(carrierId: string): Member[];
  activeAdminCount: number;
  reload(): void;
}

const TeamContext = React.createContext<Team | null>(null);

export function TeamProvider({ children }: { children: React.ReactNode }) {
  const state = useLiveQuery<Member[]>(
    async (client, agencyId) => {
      const { data, error } = await client.rpc('agency_team', { p_agency: agencyId });
      if (error) return { error: asRpcError(error)! };
      return { data: asRows(data).map(toMember) };
    },
    [],
  );

  const members = React.useMemo(() => (state.status === 'ready' ? state.data : []), [state]);
  const { reload } = state;

  const value = React.useMemo<Team>(() => {
    const byId = new Map(members.map((m) => [m.userId, m]));
    return {
      status: state.status,
      members,
      byId,
      nameOf: (userId) => {
        if (!userId) return null;
        const m = byId.get(userId);
        return m ? (m.displayName ?? m.email) : null;
      },
      dispatchersOf: (carrierId) =>
        members.filter((m) => m.role === 'dispatcher' && m.status === 'active' && m.carrierIds.includes(carrierId)),
      activeAdminCount: activeAdminCount(members),
      reload,
    };
  }, [members, state.status, reload]);

  return <TeamContext.Provider value={value}>{children}</TeamContext.Provider>;
}

export function useTeam(): Team {
  const ctx = React.useContext(TeamContext);
  if (!ctx) throw new Error('useTeam must be used inside TeamProvider');
  return ctx;
}
